import { Box, Card, Skeleton, Typography } from '@mui/material';
import { SchoolOutlined, TimerOutlined, WorkOutlineOutlined } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/system';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import type { JobTitleCoursesKpisI } from 'Api/axios/job-title-courses-report-api';

interface Props {
  kpis: JobTitleCoursesKpisI | null;
  loading: boolean;
}

const styles: Record<string, SxProps<Theme>> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
    gap: 2,
    mb: 2.5,
  },
  card: {
    p: 2.25,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    borderRadius: 2,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    border: `1px solid ${colors.GRAY_HR}`,
  },
  value: { fontSize: 22, fontWeight: 700, color: colors.BLACK, lineHeight: 1.1 },
  label: { fontSize: 12.5, color: colors.GRAY_DARK, mt: 0.5 },
  sublabel: { fontSize: 11, color: colors.GRAY_DARK, mt: 0.25 },
};

const iconWrapStyle = (tint: string): SxProps<Theme> => ({
  width: 42,
  height: 42,
  borderRadius: 1.25,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
  background: `${tint}1A`,
  color: tint,
});

/**
 * Indicadores del Reporte de Cursos por Puesto.
 *
 * Presentacional puro: los números llegan calculados del backend sobre el total
 * filtrado, no sobre la página visible. El front nunca agrega.
 */
export default function JobTitleCoursesKpis({ kpis, loading }: Props) {
  const { t2 } = useTranslation('JobTitleCoursesReport');
  const hasHours = (kpis?.coursesWithHours ?? 0) > 0;

  const items = [
    {
      key: 'jobTitles',
      tint: colors.GREEN,
      icon: <WorkOutlineOutlined />,
      value: kpis?.totalJobTitles ?? 0,
      label: t2('kpis.totalJobTitles'),
    },
    {
      key: 'courses',
      tint: colors.GREEN_LEAF,
      icon: <SchoolOutlined />,
      value: kpis?.totalCourses ?? 0,
      label: t2('kpis.totalCourses'),
      // Deja visible el conteo con repeticiones sin volverlo el titular: si el
      // negocio prefiere ese criterio, es cambiar qué campo va arriba.
      sublabel: t2('kpis.totalAssignments', [String(kpis?.totalAssignments ?? 0)]),
    },
    {
      key: 'hours',
      tint: colors.BLUE,
      icon: <TimerOutlined />,
      // Sin horas capturadas, un "0h" se leería como "no requiere entrenamiento".
      // Se muestra un guion y se avisa que falta capturar el dato.
      value: hasHours ? `${kpis?.totalTrainingHours}h` : '—',
      label: t2('kpis.totalTrainingHours'),
      sublabel: hasHours ? undefined : (t2('kpis.noHoursCaptured') as string),
    },
  ];

  return (
    <Box sx={styles.grid}>
      {items.map(item => (
        <Card key={item.key} sx={styles.card} elevation={0}>
          <Box sx={iconWrapStyle(item.tint)}>{item.icon}</Box>
          <Box>
            {loading && !kpis ? (
              <Skeleton variant="text" width={60} height={28} />
            ) : (
              <Typography sx={styles.value}>{item.value}</Typography>
            )}
            <Typography sx={styles.label}>{item.label}</Typography>
            {item.sublabel && <Typography sx={styles.sublabel}>{item.sublabel}</Typography>}
          </Box>
        </Card>
      ))}
    </Box>
  );
}
